import type React from 'react';

export type CollabConnectionState =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'read-only'
  | 'forced-close';

interface Props {
  state: CollabConnectionState;
  reason?: string;
}

const COPY: Record<CollabConnectionState, { label: string; tone: string }> = {
  connecting: { label: 'Connecting to live editor…', tone: 'bg-surface text-text-secondary' },
  connected: { label: 'Live editing', tone: 'bg-success/15 text-success' },
  reconnecting: {
    label: 'Reconnecting — editing is paused until the live document is synced.',
    tone: 'bg-warning/15 text-warning',
  },
  'read-only': {
    label: 'Read-only — you do not have permission to edit this artifact.',
    tone: 'bg-surface/40 text-text-primary',
  },
  'forced-close': {
    label:
      'Your access to this artifact was revoked. The editor is now read-only; copy any unsaved local edits before reloading.',
    tone: 'bg-error/15 text-error',
  },
};

export function ConnectionStateBanner({ state, reason }: Props): React.ReactElement | null {
  if (state === 'connected') return null;
  const { label, tone } = COPY[state];
  return (
    <div
      role={state === 'forced-close' ? 'alert' : 'status'}
      data-testid={`collab-state-${state}`}
      className={`rounded-md p-2 text-xs ${tone}`}
    >
      {label}
      {reason !== undefined && reason.length > 0 ? ` (${reason})` : ''}
    </div>
  );
}
